import { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import type { CloseStatusResponse } from '../../types/shared';
import { recordEmptySubmission } from '../../api/inventory';

interface Props {
  warehouse:    CloseStatusResponse['warehouses']['outstanding'][number];
  period:       string; // 'YYYY-MM'
  onResolved:   () => void;
  /** Disabled when the period is approved (re-open required first). */
  disabled?:    boolean;
}

export default function OutstandingWarehouseRow({ warehouse, period, onResolved, disabled }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState<string | null>(null);

  async function handleMarkEmpty() {
    if (disabled) return;
    setSubmitting(true);
    setError(null);
    try {
      await recordEmptySubmission({ warehouseId: warehouse.warehouseId, periodMonth: period });
      onResolved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark empty.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-stone-100 dark:border-stone-800 bg-stone-50 dark:bg-[#1a201d]">
      <div className="flex items-center gap-2 text-sm">
        <span
          className={`text-[10px] font-medium uppercase tracking-wide rounded px-1.5 py-0.5 ${
            warehouse.type === 'hub'
              ? 'bg-moss-100 text-moss-700 dark:bg-moss-900/30 dark:text-moss-300'
              : 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400'
          }`}
        >
          {warehouse.type}
        </span>
        <span className="text-stone-800 dark:text-stone-200">{warehouse.name}</span>
        {warehouse.code && (
          <span className="text-xs text-stone-400 dark:text-stone-500">{warehouse.code}</span>
        )}
      </div>
      <div className="flex items-center gap-3">
        {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
        <button
          onClick={handleMarkEmpty}
          disabled={submitting || disabled}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border border-stone-300 dark:border-stone-600 text-stone-700 dark:text-stone-300 hover:bg-white dark:hover:bg-[#242d28] transition disabled:opacity-50"
          title={disabled ? 'Period is approved — re-open before marking empty' : 'Mark this warehouse as having nothing to submit'}
        >
          {submitting
            ? <Loader2 size={12} className="animate-spin" />
            : <Check size={12} />}
          Mark nothing to submit
        </button>
      </div>
    </div>
  );
}
